import { useState } from "react";
import { Check, X, Printer, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type { Order } from "./OrderDetailModal";

interface BulkActionsBarProps {
  selectedCount: number;
  selectedOrders: Order[];
  onMarkFulfilled: () => Promise<void>;
  onMarkUnfulfilled: () => Promise<void>;
  onExportSelected: () => void;
  onPrintPackingSlips: () => void;
  onClearSelection: () => void;
}

export function BulkActionsBar({
  selectedCount,
  selectedOrders,
  onMarkFulfilled,
  onMarkUnfulfilled,
  onExportSelected,
  onPrintPackingSlips,
  onClearSelection,
}: BulkActionsBarProps) {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleMarkFulfilled = async () => {
    setIsUpdating(true);
    try {
      await onMarkFulfilled();
      toast.success(`Marked ${selectedCount} orders as fulfilled`);
    } catch {
      toast.error("Failed to update orders");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkUnfulfilled = async () => {
    setIsUpdating(true);
    try {
      await onMarkUnfulfilled();
      toast.success(`Marked ${selectedCount} orders as unfulfilled`);
    } catch {
      toast.error("Failed to update orders");
    } finally {
      setIsUpdating(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
      <div className="bg-slate-800/95 backdrop-blur-md border border-slate-700 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3">
        <div className="flex items-center gap-2 pr-3 border-r border-slate-700">
          <span className="text-emerald-400 font-semibold">{selectedCount}</span>
          <span className="text-slate-300 text-sm">selected</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-slate-100"
            onClick={onClearSelection}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleMarkFulfilled}
          disabled={isUpdating}
          className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          Mark Fulfilled
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleMarkUnfulfilled}
          disabled={isUpdating}
          className="border-amber-700 text-amber-400 hover:bg-amber-900/20"
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <X className="h-4 w-4 mr-1" />
          )}
          Mark Unfulfilled
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={onPrintPackingSlips}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <Printer className="h-4 w-4 mr-1" />
          Print Slips
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={onExportSelected}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <Download className="h-4 w-4 mr-1" />
          Export
        </Button>
      </div>
    </div>
  );
}